import { useEffect } from 'react';
import { Router } from 'next/router';
import globalStyle from 'presentations/views/styles/global';
import ThemeProvider from 'presentations/views/components/ThemeProvider';
import { changeLanguage } from 'infras/i18n';
import {
  setDimension,
  setGroup,
  setUserId,
} from 'presentations/views/helpers/analytics';
import { ViewerItem } from 'presentations/api/schema/user';
import dynamic from 'next/dynamic';
import { Global, CacheProvider, EmotionCache } from '@emotion/react';
const cache = createEmotionCache();

const ErrorPage = dynamic(() =>
  import('presentations/views/components/pages/ErrorPage'),
);
const DashboardPortalProvider = dynamic(() =>
  import('presentations/reducers/DashboardPortal').then(
    (m: any) => m.DashboardPortalProvider,
  ),
);
const ServicePortalProvider = dynamic(() =>
  import('presentations/reducers/ServicePortal').then(
    (m: any) => m.ServicePortalProvider,
  ),
);
import { LiveChatLoaderProvider } from 'react-live-chat-loader';

import 'presentations/views/styles/reset.css';
// import 'froala-editor/css/froala_style.min.css';
// import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'react-quill/dist/quill.snow.css';
// import 'slick-carousel/slick/slick.css';
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import 'emoji-mart/css/emoji-mart.css';
import 'presentations/views/components/atoms/RichEditor/default.scss';
import 'presentations/views/components/atoms/RichEditor/agenda.scss';
import 'presentations/views/components/atoms/StaticText/default.scss';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import 'react-phone-input-2/lib/bootstrap.css';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

import {
  inDashboardUrl,
  inInternalUrl,
  isAboutUrl,
  isLandingUrl,
} from 'presentations/routes/helper';
import { currentLocale } from 'infras/datetime';
import createEmotionCache from 'infras/emotion/createEmotionCache';

declare global {
  interface Window {
    intercomSettings: any;
    workbox: any;
    newrelic: any;
  }
}

interface PageProps {
  locale?: string;
  statusCode?: number;
  viewer?: ViewerItem;
}

interface Props {
  Component: any;
  pageProps: PageProps;
  router: Router;
  emotionCache?: EmotionCache;
}

const App = (props: Props) => {
  const { Component, pageProps, router, emotionCache = cache } = props;
  const { locale, statusCode, viewer } = pageProps;

  changeLanguage(router.locale || locale || currentLocale());

  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles && jssStyles.parentElement) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
    if (window.workbox) {
      window.workbox.__WB_DISABLE_DEV_LOGS = true;
    }
  }, []);

  useEffect(() => {
    if (viewer) {
      setDimension('plan', viewer.planId);
      setUserId(viewer.uid);
      if (viewer.team) {
        setGroup('teamId', viewer.team.uid);
        setGroup('planId', String(viewer.planId));
        setGroup('teamUserRole', String(viewer.role));
      }

      const settings: any = window.intercomSettings || {};
      if (viewer && typeof viewer.intercomUserHash === 'string') {
        settings['user_hash'] = viewer.intercomUserHash;
        settings['user_id'] = viewer.uid;
        settings['email'] = viewer.email;
        settings['name'] = viewer.name;
        settings['avatar'] = {
          type: 'avatar',
          image_url: viewer.profileImage,
        };
        if (viewer.team) {
          settings['company'] = {
            company_id: viewer.team.uid,
            name: viewer.team.name,
            plan: String(viewer.planId),
          };
        }
      }
      window.intercomSettings = settings;
    }
  }, []);

  if (!process.env['INTERCOM_APP_ID']) {
    console.log('Intercom API Key is not set.');
  }
  const intercomProps: { providerKey: string; provider: 'intercom' } = {
    providerKey:
      String(process.env['INTERCOM_APP_ID']) || 'no-api-key-provided',
    provider: 'intercom',
  };

  const isDashboard = inDashboardUrl(router.asPath);
  const isLanding =
    isLandingUrl(router.pathname) || isAboutUrl(router.pathname);
  const isInternal = inInternalUrl(router.asPath);

  return (
    <CacheProvider value={emotionCache}>
      {isDashboard || isInternal ? (
        <ThemeProvider>
          <Global styles={globalStyle({ isDarkable: isDashboard })} />
          {statusCode && statusCode !== 200 ? (
            <ErrorPage statusCode={statusCode} />
          ) : (
            <LiveChatLoaderProvider {...intercomProps}>
              <DashboardPortalProvider>
                <Component {...pageProps} />
              </DashboardPortalProvider>
            </LiveChatLoaderProvider>
          )}
        </ThemeProvider>
      ) : (
        <ThemeProvider service={isLanding ? 'gray' : 'www'}>
          <Global styles={globalStyle({ isDarkable: false, isLanding })} />
          {statusCode && statusCode !== 200 ? (
            <ErrorPage statusCode={statusCode} />
          ) : (
            <LiveChatLoaderProvider {...intercomProps}>
              <ServicePortalProvider>
                <Component {...pageProps} />
              </ServicePortalProvider>
            </LiveChatLoaderProvider>
          )}
        </ThemeProvider>
      )}
    </CacheProvider>
  );
};

export default App;
